import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardFooter,
  Button,
  Input,
  Spinner,
} from "reactstrap";
import OnlyHeader from "components/Layout/OnlyHeader";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import StaffModal from "components/Modals/StaffModal";
import PaginationComponent from "components/shared/Pagination";
import DebouncedSearch from "components/shared/DebouncedSearch";
import TableComponent from "components/shared/Table";

import {
  getAllStaff,
  deleteStaff,
  updateStaff,
} from "store/actions/staffAction";
import { addNewUser } from "store/actions/authActions";
import { toast } from "react-toastify";
import { validatePassword } from "utils/ValidatePassword";
import { validateEmail } from "utils/validateEmail";

const KitchenStaff = () => {
  const dispatch = useDispatch();
  const { uid } = useSelector((state) => state.auth);
  const { staffData, staffRestData, getAllStaffLoader, deleteLoading } =
    useSelector((state) => state.staff);
  const [errors, setErrors] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hitsPerPage, setHitsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const [modalState, setModalState] = useState({
    add: false,
    edit: false,
    delete: false,
  });

  const [selectedId, setSelectedId] = useState("");
  const [permissionId, setPermissionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "role",
  });

  const initialPermissions = useMemo(
    () => ({
      menus: {
        active: false,
        options: {
          get: true,
          add: false,
          edit: false,
          delete: false,
          export: false,
          visibility: false,
        },
        category: {
          active: false,
          options: {
            get: true,
            add: false,
            edit: false,
            delete: false,
            visibility: false,
            duplicate: false,
            import: false,
          },
          items: {
            active: false,
            options: {
              get: true,
              add: false,
              edit: false,
              delete: false,
              visibility: false,
              duplicate: false,
            },
          },
        },
        label: {
          active: false,
          options: { get: true, add: false, edit: false, delete: false },
        },
        addOns: {
          active: false,
          options: { get: true, add: false, edit: false, delete: false },
        },
      },
      orders: {
        active: false,
        options: { get: true, edit: false },
      },
      requests: {
        active: false,
        options: { get: true, edit: false },
      },
      catalog: {
        active: false,
        options: { get: true, add: false, edit: false, delete: false },
      },
      customization: {
        active: false,
        options: { get: true, add: false, edit: false },
      },
      notifications: {
        active: false,
        options: { get: true, edit: false },
      },
      customers: {
        active: false,
        options: { get: true },
      },
    }),
    []
  );

  const [permissions, setPermissions] = useState(initialPermissions);

  const roles = useMemo(
    () => [
      { value: "role", label: "Select Role", disabled: true },
      { value: "kitchen-admin", label: "Admin" },
      { value: "kitchen-cook", label: "Cook" },
      { value: "kitchen-waiter", label: "Waiter" },
    ],
    []
  );

  const toggleModal = (type) => {
    setModalState((prev) => ({ ...prev, [type]: !prev[type] }));
    if (type === "add") resetForm();
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", password: "", role: "role" });
    setPermissions(initialPermissions);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    const email = formData?.email;
    if (email) {
      const emailError = validateEmail(formData?.email);
      if (emailError) {
        toast.error(emailError);
        return;
      }
    }

    const validationErrors = validatePassword(formData.password);

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    } else {
      setErrors("");
    }
    setLoading(true);
    const { role, ...rest } = formData;
    const newUser = {
      ...rest,
      restaurantID: uid,
      type: role,
    };
    dispatch(
      addNewUser(newUser, permissions, () => {
        toggleModal("add");
        setTimeout(() => {
          dispatch(getAllStaff(uid, searchTerm, hitsPerPage, currentPage));
        }, 3000);
      })
    ).finally(() => setLoading(false));
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    const fullName = formData.lastName
      ? `${formData.name} ${formData.lastName}`
      : formData.name;
    const updated = {
      name: fullName,
      type: formData.role,
    };
    dispatch(
      updateStaff(selectedId, updated, permissionId, permissions, () => {
        toggleModal("edit");
        setLoading(false);
        setTimeout(() => {
          dispatch(getAllStaff(uid, searchTerm, hitsPerPage));
        }, 3000);
      })
    );
  };

  const handleEditClick = (item) => {
    setSelectedId(item.id);
    setPermissionId(item.permissionId);
    setFormData({
      name: item.name,
      email: item.email,
      role: item.type,
    });
    setPermissions(item?.permissions);
    toggleModal("edit");
  };

  const handleDelete = () => {
    dispatch(
      deleteStaff(selectedId, permissionId, () => {
        toggleModal("delete");
        setTimeout(() => {
          dispatch(getAllStaff(uid, searchTerm, hitsPerPage, currentPage));
        }, 3000);
      })
    );
  };

  const renderTableBody = () => {
    if (!staffData?.length) {
      return (
        <tbody>
          <tr>
            <td colSpan="3" className="text-center py-5">
              No Staff to display
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <>
        <thead className="thead-light">
          <tr>
            <th>Full Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {staffData.map((item, idx) => (
            <tr key={idx}>
              <td className="text-capitalize">{item?.name}</td>
              <td>{item?.email}</td>
              <td className="text-capitalize">{item?.type}</td>
              <td>
                <Button
                  className="btn-sm mr-2"
                  color="primary"
                  onClick={() => handleEditClick(item)}
                >
                  Edit
                </Button>
                <Button
                  className="btn-sm"
                  color="danger"
                  onClick={() => {
                    setSelectedId(item.id);
                    setPermissionId(item?.objectID);
                    toggleModal("delete");
                  }}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </>
    );
  };

  useEffect(() => {
    dispatch(getAllStaff(uid, searchTerm, hitsPerPage, currentPage)).finally(
      () => setInitialLoad(false)
    );
  }, [dispatch, uid, searchTerm, hitsPerPage, currentPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7 mb-5" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader>
                <Row>
                  <Col xs="12" lg="4">
                    <h3 className="pt-2 mr-2">Staff</h3>
                  </Col>
                  <Col xs="12" lg="3">
                    <DebouncedSearch
                      placeholder="Search by name or email"
                      onSearch={setSearchTerm}
                    />
                  </Col>
                  <Col xs="12" lg="3" className="my-2 my-lg-0">
                    <Input
                      type="select"
                      value={hitsPerPage}
                      onChange={(e) => setHitsPerPage(Number(e.target.value))}
                    >
                      <option value="5">Show Entries</option>
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </Input>
                  </Col>
                  <Col xs="12" lg="2">
                    <Button
                      color="primary"
                      block
                      onClick={() => toggleModal("add")}
                    >
                      Add
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              {initialLoad && getAllStaffLoader ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner size="md" color="primary" />
                </div>
              ) : (
                <TableComponent>{renderTableBody()}</TableComponent>
              )}

              <CardFooter>
                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={staffRestData?.nbPages || 0}
                  onPageChange={setCurrentPage}
                />
              </CardFooter>
            </Card>
          </Col>
        </Row>

        <StaffModal
          isOpen={modalState.add || modalState.edit}
          toggle={() => toggleModal(modalState.add ? "add" : "edit")}
          formData={formData}
          handleInputChange={handleInputChange}
          handleSubmit={modalState.add ? handleAddSubmit : handleUpdateSubmit}
          permissions={permissions}
          setPermissions={setPermissions}
          rolesList={roles}
          errors={errors}
          loading={loading}
          type={modalState.add ? "add" : "edit"}
          disabledSubmit={!!errors}
        />

        <ConfirmationModal
          title="Delete Staff"
          text="Are you sure you want to delete staff?"
          isOpen={modalState.delete}
          toggle={() => toggleModal("delete")}
          isLoading={deleteLoading}
          onConfirmation={handleDelete}
        />
      </Container>
    </>
  );
};

export default KitchenStaff;
